/**
 * Role Management Page
 *
 * Admin UI for managing roles in the RBAC system:
 * - View all roles (system + custom)
 * - Create custom roles
 * - Edit role permissions
 * - Delete custom roles
 * - View role details and user count
 */

import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { Shield, Users, Edit2, Trash2, Plus, Check, X } from 'lucide-react';

interface Role {
  id: number;
  name: string;
  display_name: string;
  description: string;
  is_system_role: boolean;
  company_id: number | null;
  user_count: number;
  permission_count: number;
  created_at: string;
}

interface Permission {
  id: number;
  key: string;
  category: string;
  description: string;
}

export const RoleManagement: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Load roles
  useEffect(() => {
    loadRoles();
    loadPermissions();
  }, []);

  const loadRoles = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/roles') as unknown as { roles: Role[] };
      setRoles(response.roles || []);
    } catch (error) {
      console.error('Failed to load roles:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPermissions = async () => {
    try {
      const response = await api.get('/admin/permissions') as unknown as { permissions: Permission[] };
      setPermissions(response.permissions || []);
    } catch (error) {
      console.error('Failed to load permissions:', error);
    }
  };

  const handleDeleteRole = async (roleId: number) => {
    if (!confirm('Are you sure you want to delete this role?')) return;

    try {
      await api.delete(`/admin/roles/${roleId}`);
      loadRoles();
    } catch (error) {
      console.error('Failed to delete role:', error);
      alert('Failed to delete role. It may still have users assigned.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading roles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="w-8 h-8 text-blue-600" />
            Role Management
          </h1>
          <p className="text-gray-600 mt-2">
            Manage user roles and their permissions
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Create Role
        </button>
      </div>

      {/* Roles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {roles.map((role) => (
          <div
            key={role.id}
            className="bg-white rounded-lg shadow-md border border-gray-200 p-6 hover:shadow-lg transition-shadow"
          >
            {/* Role Header */}
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  {role.display_name}
                  {role.is_system_role && (
                    <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                      System
                    </span>
                  )}
                </h3>
                <p className="text-sm text-gray-500 mt-1">{role.name}</p>
              </div>
              {!role.is_system_role && (
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setSelectedRole(role);
                      setShowEditModal(true);
                    }}
                    className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                    title="Edit role"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteRole(role.id)}
                    className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                    title="Delete role"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            {/* Role Description */}
            {role.description && (
              <p className="text-sm text-gray-600 mb-4">{role.description}</p>
            )}

            {/* Role Stats */}
            <div className="flex justify-between items-center pt-4 border-t border-gray-200">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Users className="w-4 h-4" />
                <span>{role.user_count} users</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Shield className="w-4 h-4" />
                <span>{role.permission_count} permissions</span>
              </div>
            </div>

            {/* View Details Button */}
            <button
              onClick={() => setSelectedRole(role)}
              className="w-full mt-4 px-4 py-2 text-sm text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
            >
              View Details
            </button>
          </div>
        ))}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <CreateEditRoleModal
          role={showEditModal ? selectedRole : null}
          permissions={permissions}
          onClose={() => {
            setShowCreateModal(false);
            setShowEditModal(false);
            setSelectedRole(null);
          }}
          onSuccess={() => {
            loadRoles();
            setShowCreateModal(false);
            setShowEditModal(false);
            setSelectedRole(null);
          }}
        />
      )}

      {/* Role Details Modal */}
      {selectedRole && !showEditModal && (
        <RoleDetailsModal
          role={selectedRole}
          onClose={() => setSelectedRole(null)}
          onEdit={() => {
            setShowEditModal(true);
          }}
        />
      )}
    </div>
  );
};

/**
 * Create/Edit Role Modal Component
 */
interface CreateEditRoleModalProps {
  role: Role | null;
  permissions: Permission[];
  onClose: () => void;
  onSuccess: () => void;
}

const CreateEditRoleModal: React.FC<CreateEditRoleModalProps> = ({
  role,
  permissions,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    name: role?.name || '',
    display_name: role?.display_name || '',
    description: role?.description || '',
    permissions: [] as string[],
  });
  const [loading, setLoading] = useState(false);
  const [loadingPermissions, setLoadingPermissions] = useState(false);

  // Load role permissions if editing
  useEffect(() => {
    if (role) {
      loadRolePermissions(role.id);
    }
  }, [role]);

  const loadRolePermissions = async (roleId: number) => {
    try {
      setLoadingPermissions(true);
      const response = await api.get(`/admin/roles/${roleId}`) as unknown as {
        role: { permissions: Permission[] };
      };
      if (response.role && response.role.permissions) {
        const permKeys = response.role.permissions.map((p: Permission) => p.key);
        setFormData(prev => ({ ...prev, permissions: permKeys }));
      }
    } catch (error) {
      console.error('Failed to load role permissions:', error);
    } finally {
      setLoadingPermissions(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (role) {
        // Update existing role
        await api.put(`/admin/roles/${role.id}`, formData);
      } else {
        // Create new role
        await api.post('/admin/roles', formData);
      }
      onSuccess();
    } catch (error) {
      console.error('Failed to save role:', error);
      alert('Failed to save role. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const togglePermission = (permKey: string) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permKey)
        ? prev.permissions.filter(p => p !== permKey)
        : [...prev.permissions, permKey],
    }));
  };

  // Group permissions by category
  const permissionsByCategory = permissions.reduce((acc, perm) => {
    if (!acc[perm.category]) {
      acc[perm.category] = [];
    }
    acc[perm.category].push(perm);
    return acc;
  }, {} as Record<string, Permission[]>);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">
              {role ? 'Edit Role' : 'Create New Role'}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Form Content */}
          <div className="p-6 space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role Name (internal)
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., custom_analyst"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  disabled={!!role}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Lowercase with underscores only. Cannot be changed after creation.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Name
                </label>
                <input
                  type="text"
                  value={formData.display_name}
                  onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                  placeholder="e.g., Data Analyst"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe this role..."
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Permissions */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Permissions</h3>
              {loadingPermissions ? (
                <p className="text-gray-600">Loading permissions...</p>
              ) : (
                <div className="space-y-4">
                  {Object.entries(permissionsByCategory).map(([category, perms]) => (
                    <div key={category} className="border border-gray-200 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-3 capitalize">{category}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {perms.map((perm) => (
                          <label
                            key={perm.key}
                            className="flex items-start gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
                          >
                            <input
                              type="checkbox"
                              checked={formData.permissions.includes(perm.key)}
                              onChange={() => togglePermission(perm.key)}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900">{perm.key}</div>
                              {perm.description && (
                                <div className="text-xs text-gray-500">{perm.description}</div>
                              )}
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {loading ? 'Saving...' : role ? 'Update Role' : 'Create Role'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/**
 * Role Details Modal Component
 */
interface RoleDetailsModalProps {
  role: Role;
  onClose: () => void;
  onEdit: () => void;
}

const RoleDetailsModal: React.FC<RoleDetailsModalProps> = ({ role, onClose, onEdit }) => {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRoleDetails();
  }, [role.id]);

  const loadRoleDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/roles/${role.id}`) as unknown as {
        role: { permissions: Permission[] };
      };
      setPermissions(response.role.permissions || []);
    } catch (error) {
      console.error('Failed to load role details:', error);
    } finally {
      setLoading(false);
    }
  };

  // Group permissions by category
  const permissionsByCategory = permissions.reduce((acc, perm) => {
    if (!acc[perm.category]) {
      acc[perm.category] = [];
    }
    acc[perm.category].push(perm);
    return acc;
  }, {} as Record<string, Permission[]>);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              {role.display_name}
              {role.is_system_role && (
                <span className="text-sm px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                  System Role
                </span>
              )}
            </h2>
            <p className="text-sm text-gray-500 mt-1">{role.name}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Description */}
          {role.description && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Description</h3>
              <p className="text-gray-600">{role.description}</p>
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-gray-600 mb-1">
                <Users className="w-4 h-4" />
                <span className="text-sm">Users</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">{role.user_count}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-gray-600 mb-1">
                <Shield className="w-4 h-4" />
                <span className="text-sm">Permissions</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">{permissions.length}</div>
            </div>
          </div>

          {/* Permissions */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Permissions</h3>
            {loading ? (
              <p className="text-gray-600">Loading permissions...</p>
            ) : (
              <div className="space-y-4">
                {Object.entries(permissionsByCategory).map(([category, perms]) => (
                  <div key={category} className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3 capitalize flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-600" />
                      {category}
                      <span className="text-sm text-gray-500">({perms.length})</span>
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {perms.map((perm) => (
                        <div key={perm.key} className="text-sm text-gray-600 pl-6">
                          • {perm.key}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Close
          </button>
          {!role.is_system_role && (
            <button
              type="button"
              onClick={onEdit}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <Edit2 className="w-4 h-4" />
              Edit Role
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
